import { ConnectionType } from 'primary-schema';
import { ConnectionSettingsState } from 'resource-gateway-schema';
import { z } from 'zod';

import { BaseItemSchema } from '../common.js';
import { type PollingSchedule } from '../feed.js';
import { type Connection } from './common.js';

export enum SftpSourceGeneration {
  Legacy = 'Legacy',
  Channel = 'Channel',
}

export enum FeedStatus {
  Enabled = 'Enabled',
  Disabled = 'Disabled',
}

/** The fields of a Resource Gateway SFTP Connection that primary's detail response passes through. */
export const ConnectionSettingsSchema = BaseItemSchema.extend({
  state: z.enum(ConnectionSettingsState),
  dataType: z.string().nullable(),
});

export type ConnectionSettings = z.infer<typeof ConnectionSettingsSchema>;

export const ResourceGatewayConnectionSftpSchema = z.object({
  host: z.string(),
  basePath: z.string(),
});

export type ResourceGatewayConnectionSftp = z.infer<typeof ResourceGatewayConnectionSftpSchema>;

/** `path` is relative to `ResourceGatewayConnectionSftp.basePath`. */
export const ConnectionSettingsSftpSchema = z.object({
  path: z.string().nullable(),
  documentType: z.string().nullable(),
});

export type ConnectionSettingsSftp = z.infer<typeof ConnectionSettingsSftpSchema>;

export const ResourceGatewayConnectionSchema = BaseItemSchema.extend({
  name: z.string(),
  description: z.string().nullable(),
  organizationId: z.string(),
});

export type ResourceGatewayConnection = z.infer<typeof ResourceGatewayConnectionSchema>;

export const ConnectionSftpResourceGatewaySchema = z.object({
  connectionType: z.literal(ConnectionType.Sftp),
  sourceGeneration: z.literal(SftpSourceGeneration.Channel),
  connection: ResourceGatewayConnectionSchema,
  connectionSftp: ResourceGatewayConnectionSftpSchema,
  connectionSettings: ConnectionSettingsSchema,
  connectionSettingsSftp: ConnectionSettingsSftpSchema,
});

export type ConnectionSftpResourceGateway = z.infer<typeof ConnectionSftpResourceGatewaySchema>;

export type ConnectionSftpLegacy = Omit<Connection, 'connectionType' | 'lastUpdated'> & {
  connectionType: ConnectionType.Sftp;
  sourceGeneration: SftpSourceGeneration.Legacy;
  created: string;
  updated: string;
  status: FeedStatus;
  filePath: string;
  pollingSchedule?: PollingSchedule;
  practiceIds: string[];
  flowIds: string[];
};

export type ConnectionSftp = ConnectionSftpLegacy | ConnectionSftpResourceGateway;

export type CreateSftpConnectionResponse = ConnectionSftpResourceGateway;

export type UpdateSftpConnectionResponse = ConnectionSftpResourceGateway;

/**
 * Raw shape returned by the resource-gateway SFTP endpoint. Identical to
 * `ConnectionSftpResourceGateway` minus the discriminator fields the SDK stamps on.
 */
export const ResourceGatewaySftpConnectionRawResponseSchema = z.object({
  connection: ResourceGatewayConnectionSchema,
  connectionSftp: ResourceGatewayConnectionSftpSchema,
  connectionSettings: ConnectionSettingsSchema,
  connectionSettingsSftp: ConnectionSettingsSftpSchema,
});

export type ResourceGatewaySftpConnectionRawResponse = z.infer<
  typeof ResourceGatewaySftpConnectionRawResponseSchema
>;
